import { motion } from "framer-motion";
import { ASSETS } from "@/lib/site";

const credentials = [
  "Psicologa clinica iscritta all'Albo",
  "Consulente in Sessuologia",
  "Esperta in Comunicazione Ipnotica",
  "Esperienza pluriennale nella disabilità",
];

const values = [
  {
    n: "01",
    title: "Apertura",
    desc: "Un luogo dove poterti raccontare senza filtri, dove ogni parte di te trova spazio e respiro.",
  },
  {
    n: "02",
    title: "Non giudizio",
    desc: "Qui non esistono storie sbagliate o emozioni di cui vergognarsi. Solo ascolto autentico, accogliente.",
  },
  {
    n: "03",
    title: "Quotidiano",
    desc: "Le difficoltà di tutti i giorni — grandi o piccole — meritano di essere accolte, ascoltate e attraversate insieme, con dolcezza e con rispetto.",
  },
];

export default function About({ standalone = false }) {
  return (
    <section
      id="chi-sono"
      data-testid="about-section"
      className={`${standalone ? "pt-32 pb-24" : "py-20 lg:py-32"} bg-[#E9EFE4] relative overflow-hidden`}
    >
      <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#DAE3D2] -z-0" />
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#F6F8F3] -z-0" />

      <div className="max-w-5xl mx-auto px-6 lg:px-12 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center"
        >
          <img
            src={ASSETS.logo}
            alt="Chiara Maiolo Psicologa"
            className="mx-auto h-24 lg:h-32 w-auto object-contain mb-6 opacity-90"
            data-testid="about-logo"
          />
          <span className="eyebrow" data-testid="about-eyebrow">Chi sono</span>
          <h2
            className="font-serif-elegant mt-6 text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#3D3B3A] leading-[1.05]"
            data-testid="about-title"
          >
            Dott.ssa <span className="italic text-[#6B8568]">Chiara Maiolo</span>
          </h2>
          <p className="mt-6 text-sm tracking-widest uppercase text-[#6B8568]">
            Psicologa clinica · Sessuologa Clinica · Esperta in Comunicazione Ipnotica
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
          className="mt-16 lg:mt-20 max-w-3xl mx-auto space-y-6 text-[#73706E] text-base lg:text-lg leading-[1.9]"
        >
          <p className="first-letter:font-serif-elegant first-letter:text-6xl first-letter:lg:text-7xl first-letter:float-left first-letter:mr-3 first-letter:leading-none first-letter:text-[#6B8568]">
            Sono una psicologa clinica e sessuologa clinica, con una
            specializzazione in comunicazione ipnotica. Nel mio lavoro
            accompagno le persone nell&apos;esplorazione di sé, dei propri vissuti
            emotivi e relazionali, offrendo uno spazio in cui potersi fermare,
            ascoltare e ritrovare equilibrio.
          </p>
          <p>
            Ho maturato una lunga esperienza a contatto con situazioni di
            fragilità e complessità, che mi ha insegnato il valore profondo
            dell&apos;ascolto autentico: quello che non interrompe, non giudica
            e non ha fretta di arrivare a conclusioni.
          </p>
          <p>
            Credo che ogni percorso psicologico debba essere prima di tutto
            <strong className="text-[#3D3B3A]"> un luogo sicuro</strong>, in cui potersi esprimere
            liberamente, anche quando si porta con sé ciò che fa paura o che è
            difficile nominare. Uno spazio in cui le difficoltà quotidiane —
            grandi o piccole che siano — possano essere accolte e comprese con
            rispetto e attenzione.
          </p>
          <p>
            Il mio approccio è caldo, concreto e aperto, libero da giudizi e
            preconcetti. Lavoro con singoli, coppie e famiglie, rispettando i
            tempi e i ritmi di ciascuno, perché ogni percorso è unico e merita
            di essere ascoltato nella sua specificità.
          </p>
          <p className="font-serif-elegant italic text-2xl lg:text-3xl text-[#6B8568] text-center pt-6 leading-snug">
            « Ogni storia ha il diritto di essere ascoltata,<br />
            esattamente come è. »
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          className="mt-20 grid sm:grid-cols-3 gap-px bg-[#CFD9C5] border border-[#CFD9C5] rounded-3xl overflow-hidden"
        >
          {values.map((v) => (
            <div key={v.n} className="bg-[#F6F8F3] p-8 lg:p-10" data-testid={`about-value-${v.n}`}>
              <span className="font-serif-elegant text-3xl text-[#9DB39A]">{v.n}</span>
              <h3 className="font-serif-elegant text-2xl text-[#3D3B3A] mt-3">{v.title}</h3>
              <p className="mt-3 text-sm text-[#73706E] leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
          className="mt-16 max-w-3xl mx-auto"
        >
          <p className="eyebrow text-center mb-6">Formazione & titoli</p>
          <div className="flex flex-wrap justify-center gap-3" data-testid="about-credentials">
            {credentials.map((c) => (
              <div
                key={c}
                className="flex items-center gap-3 bg-[#F6F8F3] border border-[#CFD9C5] rounded-full px-5 py-3"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#9DB39A]" />
                <span className="text-sm text-[#3D3B3A]">{c}</span>
              </div>
            ))}
          </div>
          <p className="mt-12 font-serif-elegant italic text-2xl text-[#6B8568] text-center" data-testid="about-signature">
            — Chiara
          </p>
        </motion.div>
      </div>
    </section>
  );
}
